import { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react'

interface FadeViewProps {
  visible: boolean
  duration?: number
  /**
   * Delay (ms) before starting the fade-in. Use this to sequence transitions
   * so this view only fades in after another view has finished fading out
   * (e.g. pass a delay equal to the other view's `duration`). Has no effect
   * on fade-out, which always starts immediately.
   */
  delay?: number
  className?: string
  style?: CSSProperties
  children: ReactNode
}

/**
 * Fades children in/out via opacity. Keeps the element mounted during the
 * fade-out transition, then applies `display: none` on transition end so it
 * no longer takes up layout space while hidden.
 */
export const FadeView: React.FC<FadeViewProps> = ({
  visible,
  duration = 300,
  delay = 0,
  className,
  style,
  children,
}) => {
  // Whether the element occupies layout space (i.e. not display: none).
  const [rendered, setRendered] = useState<boolean>(visible)
  // Always start transparent so a view that mounts already-visible (e.g. the
  // confirm view on first entry) still fades in instead of snapping to full
  // opacity and overlapping the outgoing view.
  const [active, setActive] = useState<boolean>(false)
  const rafRef = useRef<number>(0)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => {
    if (visible) {
      // Wait `delay` ms (to let another view finish fading out) before
      // mounting and starting the fade-in.
      timeoutRef.current = setTimeout(() => {
        // Step 1: take up space with opacity 0, then step 2 (next frame)
        // flip opacity to 1 so the transition actually runs.
        setRendered(true)
        rafRef.current = requestAnimationFrame(() => setActive(true))
      }, delay)
    } else {
      // Start fading out immediately; display: none is applied on transition end.
      setActive(false)
    }

    return () => {
      cancelAnimationFrame(rafRef.current)
      clearTimeout(timeoutRef.current)
    }
  }, [visible, delay])

  const handleTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    // Ignore transitionend events bubbling up from antd children (Select,
    // TreeSelect, validation messages, etc.); only react to this container's
    // own opacity fade, otherwise a child's fade can hide the view mid-cross-
    // fade and leave a residual image of the other view.
    if (e.target !== e.currentTarget) return
    if (e.propertyName === 'opacity' && !visible) {
      setRendered(false)
    }
  }

  return (
    <div
      className={className}
      onTransitionEnd={handleTransitionEnd}
      style={{
        ...style,
        opacity: active ? 1 : 0,
        // Reuse antd's easing curve to stay consistent with antd components.
        transition: `opacity ${duration}ms var(--ant-motion-ease-in-out)`,
        display: rendered ? (style?.display ?? undefined) : 'none',
      }}
    >
      {children}
    </div>
  )
}
